import { useEffect } from 'react';

// Get reCAPTCHA site key from environment variables
const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY || '';
let recaptchaReadyPromise: Promise<boolean> | null = null;

function loadRecaptcha(siteKey: string): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);

  if (recaptchaReadyPromise) return recaptchaReadyPromise;

  recaptchaReadyPromise = new Promise<boolean>((resolve) => {
    // If already available, resolve immediately
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => resolve(true));
      return;
    }

    const existingScript = document.getElementById('recaptcha-script') as HTMLScriptElement | null;

    const onReady = () => {
      window.grecaptcha.ready(() => resolve(true));
    };

    if (existingScript) {
      // Script already added, just wait for it
      existingScript.addEventListener('load', onReady);
      existingScript.addEventListener('error', () => resolve(false));
      return;
    }

    // Create script
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = onReady;
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });

  return recaptchaReadyPromise;
}

/**
 * Explicitly renders an invisible reCAPTCHA badge inline into the given container.
 *
 * reCAPTCHA's api.js only auto-renders `.g-recaptcha` elements once — at the moment the
 * script first loads. During client-side (SPA) navigation the badge container unmounts
 * and remounts, but the script is never reloaded, so the auto-render does not run again
 * and the badge stays missing until a full page refresh. Rendering explicitly on every
 * mount keeps the badge visible after in-app navigation.
 *
 * Returns the reCAPTCHA widget id, or null if it could not be rendered.
 */
export async function renderInlineBadge(container: HTMLElement): Promise<number | null> {
  if (!SITE_KEY || typeof window === 'undefined') return null;

  const ready = await loadRecaptcha(SITE_KEY);
  if (!ready || !window.grecaptcha?.render) return null;

  // Component may have unmounted while waiting for reCAPTCHA; don't render into a detached node.
  if (!container.isConnected) return null;

  // Guard against rendering twice into the same element (grecaptcha throws in that case).
  if (container.childElementCount > 0) return null;

  try {
    return window.grecaptcha.render(container, {
      sitekey: SITE_KEY,
      size: 'invisible',
      badge: 'inline',
    });
  } catch {
    // grecaptcha throws if the element was already rendered — safe to ignore.
    return null;
  }
}

export const useCaptchaGeneration = (action: string, enabled = true) => {
  useEffect(() => {
    if (!SITE_KEY || !enabled) return;
    loadRecaptcha(SITE_KEY);
  }, [enabled]);

  const captchaToken = async (): Promise<string | null> => {
    if (!SITE_KEY || !enabled) return null;

    const ready = await loadRecaptcha(SITE_KEY);
    if (!ready) return null;

    return window.grecaptcha.execute(SITE_KEY, { action });
  };

  return { captchaToken };
};
